/**
 * /control/convenios · Listado + Detalle M-11.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { ROLES } from "../../lib/roles";
import {
    CM_ESTADOS, TIPO_MOD_LABEL, fmtFecha, fmtMXN,
} from "./_common";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter,
    DialogHeader, DialogTitle, DialogTrigger,
} from "../../components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { FileSignature, Plus, FileDown, Check, AlertTriangle } from "lucide-react";

export function ConveniosList() {
    const [items, setItems] = useState([]);
    const [filtroEstado, setFiltroEstado] = useState("todos");
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({
        fideicomiso_id: "", tipo_modificacion: "otros_operativos",
        descripcion: "", clausulas_afectadas: "", delta_patrimonio_pct: 0,
    });
    const [fids, setFids] = useState([]);
    const nav = useNavigate();

    const load = async () => {
        const p = {};
        if (filtroEstado !== "todos") p.estado = filtroEstado;
        const r = await api.get("/control/convenios", { params: p });
        setItems(r.data);
    };
    const loadFids = async () => {
        try {
            const r = await api.get("/fideicomisos");
            const arr = Array.isArray(r.data) ? r.data : r.data.items || [];
            setFids(arr.map(f => ({ id: f.id, label: f.numero_fideicomiso || f.numero_interno || f.id })));
        } catch { setFids([]); }
    };
    useEffect(() => { load(); loadFids(); }, [filtroEstado]);

    const crear = async () => {
        if (!form.fideicomiso_id || form.descripcion.length < 10) {
            toast.error("Fideicomiso y descripción (≥10 caracteres) requeridos");
            return;
        }
        try {
            const body = {
                fideicomiso_id: form.fideicomiso_id,
                tipo_modificacion: form.tipo_modificacion,
                descripcion: form.descripcion,
                clausulas_afectadas: form.clausulas_afectadas
                    .split(",").map(s => s.trim()).filter(Boolean),
                delta_patrimonio_pct: Number(form.delta_patrimonio_pct) || 0,
            };
            const r = await api.post("/control/convenios", body);
            toast.success(`Convenio creado · ${r.data.folio_convenio}`);
            setOpen(false);
            nav(`/control/convenios/${r.data.id}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Error al crear");
        }
    };

    return (
        <div className="space-y-6" data-testid="convenios-list">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-3xl font-serif font-semibold" style={{ fontFamily: "'Fraunces', serif" }}>
                        Convenios Modificatorios · M-11
                    </h1>
                    <p className="text-sm text-muted-foreground">Modificación de fideicomisos vigentes.</p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button className="bg-[color:var(--coral)] hover:bg-[color:var(--coral)]/90 text-white"
                            data-testid="btn-nuevo-convenio">
                            <Plus className="h-4 w-4 mr-1" /> Nuevo convenio
                        </Button>
                    </DialogTrigger>
                    <DialogContent data-testid="dialog-nuevo-convenio" className="max-w-xl">
                        <DialogHeader>
                            <DialogTitle>Nuevo convenio modificatorio</DialogTitle>
                            <DialogDescription>
                                El sistema detecta automáticamente si requiere re-dictamen F-09 según M-11 §4.2.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-3">
                            <div>
                                <label className="text-xs font-semibold">Fideicomiso</label>
                                <Select value={form.fideicomiso_id}
                                    onValueChange={v => setForm({ ...form, fideicomiso_id: v })}>
                                    <SelectTrigger data-testid="sel-fideicomiso"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                                    <SelectContent>
                                        {fids.map(f => <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="text-xs font-semibold">Tipo de modificación</label>
                                <Select value={form.tipo_modificacion}
                                    onValueChange={v => setForm({ ...form, tipo_modificacion: v })}>
                                    <SelectTrigger data-testid="sel-tipo-mod"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(TIPO_MOD_LABEL).map(([k, v]) =>
                                            <SelectItem key={k} value={k}>{v}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            {form.tipo_modificacion === "cambio_patrimonio" && (
                                <div>
                                    <label className="text-xs font-semibold">Delta patrimonio (%)</label>
                                    <Input type="number" step="0.1" value={form.delta_patrimonio_pct}
                                        onChange={e => setForm({ ...form, delta_patrimonio_pct: e.target.value })} />
                                    <p className="text-xs text-muted-foreground mt-1">≥20% dispara re-dictamen automáticamente.</p>
                                </div>
                            )}
                            <div>
                                <label className="text-xs font-semibold">Descripción (≥10 caracteres)</label>
                                <Textarea value={form.descripcion}
                                    onChange={e => setForm({ ...form, descripcion: e.target.value })}
                                    rows={4} />
                            </div>
                            <div>
                                <label className="text-xs font-semibold">Cláusulas afectadas (coma)</label>
                                <Input value={form.clausulas_afectadas}
                                    onChange={e => setForm({ ...form, clausulas_afectadas: e.target.value })}
                                    placeholder="Octava, Décima" />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                            <Button onClick={crear} data-testid="btn-crear-convenio">Crear</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="flex gap-2 items-center">
                <span className="text-xs text-muted-foreground">Estado:</span>
                {["todos", "propuesto", "en_dictamen", "en_firma", "vigente", "rechazado"].map(e =>
                    <Button key={e} size="sm"
                        variant={filtroEstado === e ? "default" : "outline"}
                        onClick={() => setFiltroEstado(e)}
                        data-testid={`filtro-${e}`}
                    >{e}</Button>)}
            </div>

            <Card>
                <CardContent className="p-0">
                    <table className="w-full text-sm">
                        <thead className="bg-muted/40 text-xs uppercase">
                            <tr>
                                <th className="px-3 py-2 text-left">Folio</th>
                                <th className="px-3 py-2 text-left">Consec.</th>
                                <th className="px-3 py-2 text-left">Tipo</th>
                                <th className="px-3 py-2 text-left">Estado</th>
                                <th className="px-3 py-2 text-left">Re-dictamen</th>
                                <th className="px-3 py-2 text-left">Creado</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.length === 0 && (
                                <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">Sin convenios.</td></tr>
                            )}
                            {items.map(cm => {
                                const cfg = CM_ESTADOS[cm.estado] || { label: cm.estado, cls: "" };
                                return (
                                    <tr key={cm.id} className="border-t hover:bg-muted/20">
                                        <td className="px-3 py-2 font-mono text-xs">
                                            <Link to={`/control/convenios/${cm.id}`} className="text-primary hover:underline">
                                                {cm.folio_convenio}
                                            </Link>
                                        </td>
                                        <td className="px-3 py-2">#{cm.numero_consecutivo_fideicomiso}</td>
                                        <td className="px-3 py-2 text-xs">{TIPO_MOD_LABEL[cm.tipo_modificacion] || cm.tipo_modificacion}</td>
                                        <td className="px-3 py-2"><Badge className={cfg.cls}>{cfg.label}</Badge></td>
                                        <td className="px-3 py-2">{cm.requiere_redictamen ? "Sí" : "No"}</td>
                                        <td className="px-3 py-2 text-xs">{fmtFecha(cm.created_at)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </CardContent>
            </Card>
        </div>
    );
}


export function ConvenioDetalle() {
    const { id } = useParams();
    const { hasRole } = useAuth();
    const [cm, setCm] = useState(null);
    const puedeRedictamen = hasRole([ROLES.DIRECCION, ROLES.ADMIN_TI, ROLES.ADMINISTRADOR_FIDUCIARIO]);
    const reload = () => api.get(`/control/convenios/${id}`).then(r => setCm(r.data));
    useEffect(() => { reload(); }, [id]);
    if (!cm) return <div className="p-6 text-muted-foreground">Cargando…</div>;

    const cfg = CM_ESTADOS[cm.estado] || { label: cm.estado, cls: "" };

    const firmar = async (parte_id) => {
        try {
            await api.post(`/control/convenios/${id}/registrar-firma-parte`, {
                parte_id, fecha: new Date().toISOString(),
            });
            toast.success(`Firma registrada: ${parte_id}`);
            reload();
        } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    };
    const aprobarRedictamen = async () => {
        const sesion = prompt("ID de sesión del Comité Fiduciario:");
        if (!sesion) return;
        try {
            await api.post(`/control/convenios/${id}/aprobar-redictamen`,
                null, { params: { sesion_id: sesion } });
            toast.success("Re-dictamen aprobado");
            reload();
        } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    };
    const finalizar = async () => {
        try {
            await api.post(`/control/convenios/${id}/finalizar`);
            toast.success("Convenio vigente · PDF generado");
            reload();
        } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    };
    const descargar = () => window.open(
        `${api.defaults.baseURL}/control/convenios/${id}/pdf`, "_blank",
    );

    const partes = cm.aprobaciones?.partes || [];
    const faltanFirmas = partes.filter(p => !p.firmo).length;

    return (
        <div className="space-y-6" data-testid="convenio-detalle">
            <div>
                <div className="flex items-center gap-2 flex-wrap">
                    <h1 className="text-2xl font-serif font-semibold" style={{ fontFamily: "'Fraunces', serif" }}>
                        {cm.folio_convenio}
                    </h1>
                    <Badge className={cfg.cls}>{cfg.label}</Badge>
                    {cm.requiere_redictamen && (
                        <Badge className="bg-amber-100 text-amber-900 border-amber-300 border">
                            Re-dictamen F-09 requerido
                        </Badge>
                    )}
                    <span className="text-sm text-muted-foreground">
                        Consecutivo #{cm.numero_consecutivo_fideicomiso} · {TIPO_MOD_LABEL[cm.tipo_modificacion]}
                    </span>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="md:col-span-2">
                    <CardHeader><CardTitle className="text-base">Descripción</CardTitle></CardHeader>
                    <CardContent className="text-sm space-y-2">
                        <p>{cm.descripcion}</p>
                        {cm.clausulas_afectadas?.length > 0 && (
                            <p className="text-xs text-muted-foreground">
                                Cláusulas afectadas: <b>{cm.clausulas_afectadas.join(", ")}</b>
                            </p>
                        )}
                        {cm.delta_patrimonio_pct > 0 && (
                            <p className="text-xs text-muted-foreground">
                                Delta patrimonio: <b>{cm.delta_patrimonio_pct}%</b>
                            </p>
                        )}
                    </CardContent>
                </Card>

                <Card>
                    <CardHeader><CardTitle className="text-base">Acciones</CardTitle></CardHeader>
                    <CardContent className="space-y-2">
                        {cm.requiere_redictamen && !cm.aprobaciones?.comite_fiduciario_interno?.obtenida && puedeRedictamen && (
                            <Button className="w-full" variant="outline" onClick={aprobarRedictamen}
                                data-testid="btn-aprobar-redictamen">
                                <Check className="h-4 w-4 mr-1" /> Aprobar re-dictamen
                            </Button>
                        )}
                        {cm.estado !== "vigente" && faltanFirmas === 0 && (!cm.requiere_redictamen || cm.aprobaciones?.comite_fiduciario_interno?.obtenida) && (
                            <Button className="w-full bg-[color:var(--coral)] hover:bg-[color:var(--coral)]/90 text-white"
                                onClick={finalizar} data-testid="btn-finalizar">
                                Finalizar y generar PDF
                            </Button>
                        )}
                        {cm.estado === "vigente" && (
                            <Button className="w-full" variant="outline" onClick={descargar}
                                data-testid="btn-descargar-pdf">
                                <FileDown className="h-4 w-4 mr-1" /> Descargar PDF
                            </Button>
                        )}
                    </CardContent>
                </Card>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                        <FileSignature className="h-4 w-4" /> Partes y firmas
                        {faltanFirmas > 0 && (
                            <Badge className="bg-amber-100 text-amber-900 border-amber-300 border">
                                {faltanFirmas} pendiente(s)
                            </Badge>
                        )}
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    <table className="w-full text-sm" data-testid="tabla-firmas">
                        <thead className="bg-muted/40 text-xs uppercase">
                            <tr>
                                <th className="px-3 py-2 text-left">Parte</th>
                                <th className="px-3 py-2 text-left">Firmó</th>
                                <th className="px-3 py-2 text-left">Fecha</th>
                                <th className="px-3 py-2 text-right">Acción</th>
                            </tr>
                        </thead>
                        <tbody>
                            {partes.length === 0 && (
                                <tr><td colSpan={4} className="p-4 text-center text-muted-foreground">
                                    Sin partes registradas.
                                </td></tr>
                            )}
                            {partes.map(p => (
                                <tr key={p.parte_id} className="border-t">
                                    <td className="px-3 py-2 font-mono text-xs">{p.parte_id}</td>
                                    <td className="px-3 py-2">
                                        {p.firmo ? (
                                            <Badge className="bg-emerald-100 text-emerald-900 border-emerald-300 border">✓ Firmó</Badge>
                                        ) : (
                                            <Badge className="bg-slate-100 text-slate-800 border-slate-200 border">Pendiente</Badge>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-xs">{fmtFecha(p.fecha_firma)}</td>
                                    <td className="px-3 py-2 text-right">
                                        {!p.firmo && cm.estado !== "vigente" && (
                                            <Button size="sm" variant="outline" onClick={() => firmar(p.parte_id)}
                                                data-testid={`btn-firmar-${p.parte_id}`}>
                                                Registrar firma
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </CardContent>
            </Card>

            {cm.hash_documento && (
                <p className="text-xs text-muted-foreground">
                    Hash SHA-256 del documento: <code className="bg-muted px-1 py-0.5 rounded">{cm.hash_documento.slice(0, 32)}…</code>
                </p>
            )}
        </div>
    );
}

export default ConveniosList;
